import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipientsApi } from '@/lib/recipients/api';
import { settingsKeys } from '@/hooks/api/query-keys';

// ============ Types ============

/**
 * Indicates where a setting value came from:
 * - "system"    = hard-coded system default
 * - "company"   = company-level default
 * - "workspace" = workspace-level override
 */
export type FieldSource = 'system' | 'company' | 'workspace';

/**
 * Resolved recipient settings returned by GET /v1/recipients/settings.
 * Matches the backend's ResolvedSettings shape exactly.
 */
export interface RecipientSettings {
  id: string;
  companyId: string | null;
  workspaceId: string;
  useCompanyDefaults: boolean;
  overriddenFields: string[];

  defaultTagIds: string[];
  defaultGroupIds: string[];
  profileCompletenessThreshold: number;
  autoGroupByDepartment: boolean;
  autoGroupByLocation: boolean;
  syncConfig: Record<string, unknown> | null;

  createdAt: string;
  updatedAt: string;

  /** Per-field inheritance source badges */
  _fieldSources: Record<string, FieldSource>;
}

export interface UpdateRecipientSettingsDto {
  useCompanyDefaults?: boolean;
  defaultTagIds?: string[];
  defaultGroupIds?: string[];
  profileCompletenessThreshold?: number;
  autoGroupByDepartment?: boolean;
  autoGroupByLocation?: boolean;
}

// ============ Query Hooks ============

export function useRecipientSettings() {
  return useQuery({
    queryKey: settingsKeys.recipients(),
    queryFn: () => recipientsApi<RecipientSettings>('settings'),
    staleTime: 5 * 60 * 1000, // 5 min cache — settings rarely change
  });
}

// ============ Mutation Hooks ============

export function useUpdateRecipientSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateRecipientSettingsDto) =>
      recipientsApi<RecipientSettings>('settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(settingsKeys.recipients(), updated);
    },
  });
}

export function useResetRecipientSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      recipientsApi<RecipientSettings>('settings/reset', {
        method: 'POST',
      }),
    onSuccess: (defaults) => {
      queryClient.setQueryData(settingsKeys.recipients(), defaults);
    },
  });
}
